/**
 * IdleTimer — pure, DOM-free inactivity detector.
 *
 * Tracks user activity via a single re-armable timer. Fires `onIdle` once
 * after `timeoutMs` of no activity, then fires `onResume` on the first
 * activity that follows (and re-arms for the next idle cycle).
 *
 * No DOM/browser globals are referenced directly — timer functions are
 * injectable so this can be unit tested with Jest fake timers without jsdom.
 */
export interface IdleTimerOptions {
  /** Inactivity threshold in ms. <= 0 disables the timer entirely. */
  timeoutMs: number;
  /** Fired once when the timeout elapses with no activity. */
  onIdle: () => void;
  /** Fired on the first activity() call after onIdle has fired. */
  onResume: () => void;
  /** Injectable for tests; defaults to globalThis.setTimeout. */
  setTimeout?: typeof setTimeout;
  /** Injectable for tests; defaults to globalThis.clearTimeout. */
  clearTimeout?: typeof clearTimeout;
}

export class IdleTimer {
  private readonly timeoutMs: number;
  private readonly onIdle: () => void;
  private readonly onResume: () => void;
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;

  private readonly enabled: boolean;
  private started: boolean = false;
  private idle: boolean = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: IdleTimerOptions) {
    this.timeoutMs = opts.timeoutMs;
    this.onIdle = opts.onIdle;
    this.onResume = opts.onResume;
    this.setTimeoutFn = opts.setTimeout ?? globalThis.setTimeout.bind(globalThis);
    this.clearTimeoutFn = opts.clearTimeout ?? globalThis.clearTimeout.bind(globalThis);
    this.enabled = this.timeoutMs > 0;
  }

  get isIdle(): boolean {
    return this.idle;
  }

  /** Arms the timer. No-op if disabled. */
  start(): void {
    if (!this.enabled) return;
    this.started = true;
    this.idle = false;
    this.rearm();
  }

  /**
   * Call on every user activity. If currently idle, fires onResume() once
   * and re-arms. Otherwise just resets the countdown. No-op if disabled or
   * not started.
   */
  activity(): void {
    if (!this.enabled || !this.started) return;

    if (this.idle) {
      this.idle = false;
      this.onResume();
    }

    this.rearm();
  }

  /** Clears the timer and forgets idle state. Call start() again to resume. */
  stop(): void {
    this.started = false;
    this.idle = false;
    this.clearArmedTimer();
  }

  private rearm(): void {
    this.clearArmedTimer();
    this.timer = this.setTimeoutFn(() => {
      this.timer = null;
      this.idle = true;
      this.onIdle();
    }, this.timeoutMs);
  }

  private clearArmedTimer(): void {
    if (this.timer !== null) {
      this.clearTimeoutFn(this.timer);
      this.timer = null;
    }
  }
}
